/* eslint-disable @typescript-eslint/no-explicit-any */

/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@lib/api-client';
import { TOrganization } from '@global-types/api/api';
import Decimal from 'decimal.js';

/* -------------------------------------------------------------------------- */
/*                                     Api                                    */
/* -------------------------------------------------------------------------- */
export const getOrganizationById = (
	organizationId: string
): Promise<TOrganization> => {
	return apiClient.get(
		'/organizations/' + organizationId,
		{
			params: {
				populate:
					'brands.offsets,producers.measurabilities,producers.additionalities',
			},
		}
	);
};

/* -------------------------------------------------------------------------- */
/*                                     Hook                                   */
/* -------------------------------------------------------------------------- */
export const useGetOrganization = (
	organizationId: string
) => {
	const { data } = useQuery({
		queryKey: ['get-organization-by-id', organizationId],
		queryFn: () => getOrganizationById(organizationId),
		enabled: !!organizationId,
		staleTime: 0,
	});

	const updateListBrands = (brands: any) =>
		brands && Array.isArray(brands)
			? brands.reduce((acc: any, currentItem: any) => {
					const offsetAmount =
						currentItem?.offsets &&
						Array.isArray(currentItem?.offsets)
							? currentItem.offsets.reduce(
									(accumulator: number, curr: any) => {
										const decimal_current_meanPrice_europertco2eq =
											new Decimal(
												curr.meanPrice_europertco2eq
											);

										const decimal_accumulator =
											new Decimal(accumulator);

										const result =
											decimal_accumulator.add(
												decimal_current_meanPrice_europertco2eq
											);

										return result.toNumber();
									},
									0
								)
							: 0;

					const offsetPerTone =
						currentItem?.offsets &&
						Array.isArray(currentItem?.offsets)
							? currentItem.offsets.reduce(
									(accumulator: number, curr: any) => {
										const decimal_current_quantity_gco2eq =
											new Decimal(curr.quantity_gco2eq);

										const decimal_accumulator =
											new Decimal(accumulator);

										const result =
											decimal_accumulator.add(
												decimal_current_quantity_gco2eq
											);

										return result.toNumber();
									},
									0
								)
							: 0;

					const modifiedItem = {
						name: currentItem.name,
						id: currentItem.name,
						offsetAmount,
						offsetPerTone,
					};
					acc.push(modifiedItem);

					return acc;
				}, [])
			: [];

	return {
		data,
		brands: data?.brands
			? updateListBrands(data?.brands)
			: [],
	};
};
